/**
* Localized NAATI FAQ hub renderer — /{locale}/naati/questions.
* Hand-written per-locale Q&A (see data/naati/qa-locale.ts) rendered with a
* FAQPage + WebPage JSON-LD graph and answer-first markup for AI engines.
*/
import { Link } from "@tanstack/react-router";
import type { NaatiLocale } from "@/data/naati/locale-copy";
import { NAATI_QA_LOCALE } from "@/data/naati/qa-locale";
import { absoluteUrl, SITE } from "@/config/sites";
import { JsonLd } from "@/components/JsonLd";
import { RelatedClusters } from "@/components/RelatedClusters";
import { ContentFreshness } from "@/components/ContentFreshness";

export function NaatiLocaleQuestions({ locale }: { locale: NaatiLocale }) {
  const copy = NAATI_QA_LOCALE[locale];
  const url = absoluteUrl(`/${locale}/naati/questions`);
  const orgId = SITE.canonicalHost ? `https://${SITE.canonicalHost}/#org` : "/#org";

  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": url,
        url,
        name: copy.title,
        description: copy.description,
        inLanguage: copy.hreflang,
        isPartOf: { "@id": orgId },
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["h1", ".lead", "[data-speakable]"],
        },
      },
      {
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        inLanguage: copy.hreflang,
        mainEntity: copy.items.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "NAATI", item: absoluteUrl(`/${locale}/naati`) },
          { "@type": "ListItem", position: 2, name: copy.h1, item: url },
        ],
      },
    ],
  };

  return (
    <section className="mx-auto max-w-3xl px-4 py-10">
      <JsonLd data={graph} />
      <h1 className="text-3xl font-bold mb-4">{copy.h1}</h1>
      <p className="lead text-muted-foreground mb-8" data-speakable>{copy.lead}</p>

      <div className="space-y-6">
        {copy.items.map((item, i) => (
          <article key={i} className="border-b pb-5">
            <h2 className="text-lg font-semibold mb-2">{item.q}</h2>
            <p className="text-sm leading-relaxed" data-speakable>
              <span className="font-medium">{copy.answerLabel}: </span>
              {item.a}
            </p>
          </article>
        ))}
      </div>

      <div className="mt-10 p-6 bg-muted rounded-xl">
        <h2 className="font-semibold mb-1">{copy.ctaTitle}</h2>
        <p className="text-sm mb-3">{copy.ctaText}</p>
        <p className="text-sm">
          <a href="tel:0948958999" className="text-blue-700 font-semibold">094-895-8999</a>
          {" · LINE @Thainotary"}
        </p>
      </div>

      <nav className="mt-8 flex flex-wrap gap-4 text-sm">
        <a href={`/${locale}/naati`} className="text-blue-700 hover:underline">
          {copy.pillarLabel}
        </a>
        <Link to="/naati/questions" className="text-blue-700 hover:underline">
          {copy.thaiSetLabel}
        </Link>
      </nav>

      <p className="mt-6 text-xs text-muted-foreground">{copy.disclaimer}</p>
      <RelatedClusters locale={locale as never} exclude={["naati"]} />
      <ContentFreshness path={`/${locale}/naati/questions`} locale={locale as never} />
    </section>
  );
}
